/**
 * 워크스페이스에서 파일을 골라 첨부한다 (2026-08-22).
 *
 * 보고서는 대개 **워크스페이스에 이미 있다.** 지금은 그것을 메일에 붙이려면 내려받았다가
 * 다시 올려야 하고, 그러면 같은 파일이 두 벌이 되어 용량도 두 번 깎인다.
 *
 * ★ **기본은 내가 올린 파일만** 붙는다. 메일 서버가 첨부를 `uploadedBy = 보낸사람` 으로
 *   조회하기 때문이다(mail.routes.ts 의 compose). 남이 올린 파일 번호를 넘기면
 *   INVALID_ATTACHMENT 400 이다. 그래서 남의 파일을 **감추지 않고 흐리게 두고 이유를 그
 *   자리에 적는다** — 안 보이면 "왜 없지?" 가 되고, 보내기를 누른 뒤에야 실패를 아는 것보다
 *   지금 아는 편이 낫다.
 * ★ 2026-08-24(결재 A2) — **`allowOthers` 를 켜면 그 제약이 풀린다.** 결재 첨부는 소유자냐가
 *   아니라 **읽을 수 있느냐**로 판정하므로(`approval-attachment.ts`), 공유받은 자료·회사
 *   자료도 그대로 붙는다. 기본값은 false 라 메일·게시판 호출부는 종전과 똑같이 동작한다.
 *   ⚠ 켜는 쪽은 **서버도 같은 규칙**인지 반드시 확인할 것 — 화면만 열면 400 이 난다.
 * ★ 덜 올라간 파일(verified=false)도 같은 이유로 막힌다(ATTACHMENT_NOT_FINALIZED).
 *
 * 메일 전용이 아니다 — 게시판·채팅도 같은 요구가 있어 부르는 쪽이 결과만 받게 둔다.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Check, Clock, HardDrive, Search, Shield, User, X } from '../../icons';
import { listFolderChildren, type ListFolderOptions, type NodePublic } from '../../api/workspace';
import { formatBytes } from '../../lib/attachments';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../lib/shared-i18n';
import { fileTypeLabel } from '../../lib/file-icon';
import type { UploadedAttachment } from '../../api/files';
import './WorkspaceFilePicker.css';

type Scope = 'home' | 'folder' | 'shared' | 'company';

/** ★ 라벨은 **키**로 든다 — 값으로 들면 언어를 바꿔도 이 배열이 안 따라온다(모듈 최상위라 한 번만 만든다). */
const SCOPES: Array<{ id: Scope; labelKey: 'shared.wfp.scope_recent' | 'shared.wfp.scope_mine' | 'shared.wfp.scope_shared' | 'shared.wfp.scope_company'; Icon: typeof User }> = [
  { id: 'home', labelKey: 'shared.wfp.scope_recent', Icon: Clock },
  { id: 'folder', labelKey: 'shared.wfp.scope_mine', Icon: User },
  { id: 'shared', labelKey: 'shared.wfp.scope_shared', Icon: HardDrive },
  { id: 'company', labelKey: 'shared.wfp.scope_company', Icon: Shield },
];

const KINDS: Array<{ id: string; labelKey: 'shared.wfp.kind_all' | 'shared.wfp.kind_doc' | 'shared.wfp.kind_sheet' | 'shared.wfp.kind_pdf' | 'shared.wfp.kind_image' }> = [
  { id: 'all', labelKey: 'shared.wfp.kind_all' },
  { id: 'word', labelKey: 'shared.wfp.kind_doc' },
  { id: 'excel', labelKey: 'shared.wfp.kind_sheet' },
  { id: 'pdf', labelKey: 'shared.wfp.kind_pdf' },
  { id: 'image', labelKey: 'shared.wfp.kind_image' },
];

export interface WorkspaceFilePickerProps {
  open: boolean;
  /** 내 사용자 id — 내가 올린 파일인지 가르는 기준. 모르면 아무것도 고를 수 없다. */
  myUserId: string | null;
  /**
   * 남이 올린 파일도 고를 수 있는가 (2026-08-24, 선택 · 기본 false).
   *
   * 부르는 쪽의 **서버가 소유자 대신 읽기 권한으로 판정할 때만** 켠다(결재 첨부가 그렇다).
   * 목록에 나온다는 것은 이미 읽을 수 있다는 뜻이라, 그 위에 소유자 조건을 더 걸 이유가 없다.
   */
  allowOthers?: boolean;
  /** 이미 붙어 있는 파일 — 두 번 고르지 못하게 흐리게 둔다. */
  pickedIds?: readonly string[];
  /** 앞으로 몇 개 더 넣을 수 있나. 0 이면 확인 버튼이 잠긴다. */
  room?: number;
  onClose: () => void;
  onPick: (items: UploadedAttachment[]) => void;
}

/** 워크스페이스 목록 줄 → 첨부 모양. 스토리지 키는 목록이 주지 않는다(서버가 안 쓴다). */
function toAttachment(n: NodePublic): UploadedAttachment {
  return {
    fileId: n.id,
    name: n.name,
    mimeType: n.mimeType ?? 'application/octet-stream',
    sizeBytes: n.sizeBytes,
  };
}

export function WorkspaceFilePicker(props: WorkspaceFilePickerProps) {
  const { open, myUserId, pickedIds = [], room = Infinity, allowOthers = false, onClose, onPick } = props;
  // 언어가 바뀌면 다시 그린다 — 공용 컴포넌트의 규약(AttachmentList 와 같다).
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const [scope, setScope] = useState<Scope>('home');
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<NodePublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, NodePublic>>({});

  const already = useMemo(() => new Set(pickedIds), [pickedIds]);

  useEffect(() => {
    if (!open) { setPicked({}); setQ(''); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const opts: ListFolderOptions = { limit: 60, scope, ...(kind !== 'all' ? { kind } : {}), ...(q.trim() ? { q: q.trim() } : {}) };
    // 검색은 전체를 뒤지므로 범위가 뜻을 잃는다 — 서버 규약대로 폴더 범위로 보낸다.
    listFolderChildren('root', q.trim() ? { ...opts, scope: 'folder' } : opts)
      .then((res) => {
        if (cancelled) return;
        // 폴더는 첨부할 수 없다. 서버가 폴더를 따로 주는 판에서도, 섞여 오는 판에서도 걸러진다.
        setRows(res.children.filter((c) => c.type !== 'folder'));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : tShared('shared.wfp.load_failed'));
        setRows([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, scope, kind, q]);

  /** 왜 못 고르나 — 고를 수 있으면 null. */
  const blockedReason = useCallback((n: NodePublic): string | null => {
    if (already.has(n.id)) return tShared('shared.wfp.why_already');
    if (!n.verified) return tShared('shared.wfp.why_uploading');
    if (!allowOthers && (myUserId == null || n.uploadedBy !== myUserId)) {
      return tShared('shared.wfp.why_not_mine');
    }
    return null;
  }, [already, myUserId, allowOthers]);

  const toggle = useCallback((n: NodePublic) => {
    if (blockedReason(n)) return;
    setPicked((prev) => {
      if (prev[n.id]) { const next = { ...prev }; delete next[n.id]; return next; }
      if (Object.keys(prev).length >= room) return prev;
      return { ...prev, [n.id]: n };
    });
  }, [blockedReason, room]);

  const chosen = Object.values(picked);
  const totalBytes = chosen.reduce((sum, n) => sum + n.sizeBytes, 0);

  if (!open) return null;

  return (
    <div className="wfp-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wfp" role="dialog" aria-modal="true" aria-label={tShared('shared.wfp.title')}>
        <header className="wfp-head">
          <b>{tShared('shared.wfp.title')}</b>
          <span className="wfp-gap" />
          <button type="button" className="wfp-x" onClick={onClose} aria-label={tShared('shared.wfp.close')}><X size={16} /></button>
        </header>

        <div className="wfp-body">
          <nav className="wfp-nav" aria-label={tShared('shared.wfp.title')}>
            {SCOPES.map(({ id, labelKey, Icon }) => (
              <button
                key={id}
                type="button"
                className={`wfp-navitem${scope === id ? ' is-on' : ''}`}
                aria-pressed={scope === id}
                onClick={() => setScope(id)}
              >
                <Icon size={14} aria-hidden />{tShared(labelKey)}
              </button>
            ))}
          </nav>

          <div className="wfp-main">
            <div className="wfp-bar">
              <label className="wfp-search">
                <Search size={13} aria-hidden />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={tShared('shared.wfp.search_ph')}
                  aria-label={tShared('shared.wfp.search_aria')}
                />
              </label>
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  className={`wfp-chip${kind === k.id ? ' is-on' : ''}`}
                  aria-pressed={kind === k.id}
                  onClick={() => setKind(k.id)}
                >
                  {tShared(k.labelKey)}
                </button>
              ))}
            </div>

            <div className="wfp-list" role="listbox" aria-label={tShared('shared.wfp.list_aria')}>
              {loading && <p className="wfp-msg">{tShared('shared.wfp.loading')}</p>}
              {!loading && error && <p className="wfp-msg wfp-msg--bad" role="alert">{error}</p>}
              {!loading && !error && rows.length === 0 && <p className="wfp-msg">{tShared('shared.wfp.empty')}</p>}
              {!loading && !error && rows.map((n) => {
                const why = blockedReason(n);
                const on = !!picked[n.id];
                return (
                  <button
                    key={n.id}
                    type="button"
                    role="option"
                    aria-selected={on}
                    disabled={why !== null}
                    className={`wfp-row${on ? ' is-on' : ''}${why ? ' is-off' : ''}`}
                    onClick={() => toggle(n)}
                    title={why ?? n.name}
                  >
                    <span className={`wfp-check${on ? ' is-on' : ''}`}>{on && <Check size={11} />}</span>
                    <span className="wfp-kind">{fileTypeLabel({ name: n.name, mimeType: n.mimeType })}</span>
                    <span className="wfp-name">{n.name}</span>
                    {why && <span className="wfp-why">{why}</span>}
                    <span className="wfp-meta">{formatBytes(n.sizeBytes)}</span>
                  </button>
                );
              })}
            </div>

            <footer className="wfp-foot">
              <span>
                <b>{tShared('shared.wfp.selected', { n: chosen.length })}</b>
                {chosen.length > 0 && ` · ${formatBytes(totalBytes)}`}
              </span>
              <span className="wfp-gap" />
              <button type="button" className="wfp-btn" onClick={onClose}>{tShared('shared.wfp.cancel')}</button>
              <button
                type="button"
                className="wfp-btn wfp-btn--pri"
                disabled={chosen.length === 0}
                onClick={() => { onPick(chosen.map(toAttachment)); onClose(); }}
              >
                {tShared('shared.wfp.attach')}
              </button>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
